import { AxiosError } from 'axios';

import {
  ApiErrorEnvelope,
  ParsedApiError,
} from '../types/apiError';

const RESERVED_KEYS = new Set([
  'statusCode',
  'error',
  'message',
  'errorCode',
  'path',
  'timestamp',
  'requestId',
  'details',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function toErrorCode(value: unknown): string | undefined {
  return typeof value === 'string' && value.length > 0 ? value : undefined;
}

/**
 * Flattens custom payload: anything under `details`, plus any non-reserved
 * field that leaked to the top level. Top-level wins on key collision so
 * older/newer backend shapes both resolve.
 */
function collectDetails(
  body: Record<string, unknown>,
): Record<string, unknown> {
  const nested = isRecord(body.details) ? body.details : {};
  const topLevelExtras: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(body)) {
    if (!RESERVED_KEYS.has(key)) {
      topLevelExtras[key] = value;
    }
  }
  return { ...nested, ...topLevelExtras };
}

function normalizeMessage(raw: unknown): {
  message: string;
  fieldErrors: string[];
} {
  if (typeof raw === 'string' && raw.length > 0) {
    return { message: raw, fieldErrors: [] };
  }
  if (Array.isArray(raw)) {
    const fieldErrors = raw.filter(
      (item): item is string => typeof item === 'string',
    );
    return { message: fieldErrors.join('; '), fieldErrors };
  }
  return { message: '', fieldErrors: [] };
}

/**
 * The single entry point for reading any thrown value as a structured server
 * error. Handles the backend envelope (top-level or `details`-nested custom
 * fields), normalizes `message: string | string[]`, and resolves the
 * machine-readable `errorCode`.
 *
 * @param error  Any caught value (AxiosError, Error, or unknown).
 * @param fallback  Message to use when the server provided none.
 */
export function parseApiError(
  error: unknown,
  fallback = '',
): ParsedApiError {
  if (error instanceof AxiosError) {
    const body = (error.response?.data ?? {}) as Partial<ApiErrorEnvelope>;
    const { message, fieldErrors } = normalizeMessage(body.message);
    const details = isRecord(body)
      ? collectDetails(body as Record<string, unknown>)
      : {};
    const code =
      toErrorCode(body.errorCode) ?? toErrorCode(details.errorCode);

    return {
      statusCode: error.response?.status ?? 0,
      code,
      message: message || error.message || fallback,
      fieldErrors,
      details,
    };
  }

  if (error instanceof Error) {
    return {
      statusCode: 0,
      message: error.message || fallback,
      fieldErrors: [],
      details: {},
    };
  }

  return {
    statusCode: 0,
    message: fallback,
    fieldErrors: [],
    details: {},
  };
}
